import { VerityOptions, AssessmentInfo, Lms } from "@verity/api-client/types";
import {
	createThemeIframeHelper,
	saveAssessmentUrl,
	sendInstituteId,
	getMoodleQuizInfo,
	MOODLE_ATTRIBUTES,
	createModalIframe,
	AssessmentQueryInfo,
	initializeInstructionsIframe,
	observeAndRemoveExtensionIframes,
	listenForLTILoad,
} from "./helpers";
import checkStartAssessment from "./check-start-assessment";
import { checkReturnToAssessmentStart } from "./check-return-to-assessment-start";
import { LockdownLaunchInfo } from "../../portal/start-assessment";

export {};

declare global {
	interface Window {
		environment?: string;
		proctor?: boolean;
		courseId?: string;
	}
}

const QUIZ_MOODLE = {
	PREVIEW_URL_REGEX: /\/moodle\/mod\/quiz\/view\.php\?id=[^\/]+/,
	LIST_URL_REGEX: /\/moodle\/course\/view\.php\?id=[^\/]+/,
	ID: {
		WRAPPER_DIV: "page",
		DISPLAY: "quiz_show",
	},
};

let verityHostname: string;
let courseId: string;

function modifyMoodleQuizEntryLockdown(quizInfo: AssessmentInfo, quizOption: VerityOptions, assessmentUrl: string) {
	const lockdownInfo: LockdownLaunchInfo = {
		target: assessmentUrl,
		assessmentIdInfo:
			quizInfo.courseId && quizInfo.quizId
				? {
						...quizInfo,
						lmsProvidedCourseId: quizInfo.courseId,
						lmsProvidedAssessmentId: quizInfo.quizId,
					}
				: undefined,
		thirdPartyOptions: quizOption.thirdPartyAssessmentOptions,
	};

	const forms = Array.from(document.querySelectorAll("form")).filter((f) =>
		f.action.includes(MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_START_ATTEMPT),
	);
	if (!forms?.length) {
		return;
	}
	for (const form of forms) {
		// Replace original button with fake button
		const startQuizButton = form.querySelector("button");
		if (startQuizButton) {
			const newStartQuizButton = startQuizButton.cloneNode(true) as HTMLElement;
			newStartQuizButton.classList.add(...startQuizButton.classList);
			newStartQuizButton.textContent = startQuizButton.textContent;
			newStartQuizButton.id = "start-yuja-test-proctoring";
			newStartQuizButton.addEventListener("click", (ev) => {
				ev.preventDefault();
				ev.stopPropagation();
				ev.stopImmediatePropagation();
				createModalIframe(lockdownInfo);
			});
			startQuizButton?.parentNode?.insertBefore(newStartQuizButton, startQuizButton);
			startQuizButton.setAttribute("style", "float:left;display:none");
		}
	}
}

function showBlockPageMoodle(blockPage: string): void {
	const quizDisplay = document.getElementById("region-main-box");
	if (!quizDisplay) {
		return;
	}

	const notice = document.createElement("iframe");
	notice.id = "yuja-download-instructions-theme";
	notice.width = quizDisplay.offsetWidth.toString();
	notice.src = `https://${verityHostname}/warning/${blockPage}.html?lms=moodle`;
	notice.style.border = "none";
	notice.style.transition = "all 0.5s ease-in-out 0s";
	notice.height = "100%"; // actual height for Moodle 4.1: "671.5px";
	notice.width = "100%";
	notice.style.paddingLeft = "15px";
	notice.style.paddingRight = "15px";

	initializeInstructionsIframe(
		notice,
		() => ({
			event: "init-auth-instructions",
			authLink: window.location.href.replace(/\/mod\/quiz.*/, `/course/view.php?id=${courseId}`),
		}),
		`https://${verityHostname}`,
	);

	quizDisplay.style.display = "none";
	quizDisplay.style.visibility = "hidden";

	const topofscrollDisplay = document.getElementById("topofscroll");

	if (topofscrollDisplay) {
		//moodle 4.1+
		topofscrollDisplay.style.marginBottom = "0";
		const parent = topofscrollDisplay.parentNode;
		const pageWrapper = document.getElementById("page-wrapper");
		const pageContent = document.getElementById("page-content");

		if (pageWrapper) pageWrapper.style.overflow = "visible";
		notice.height = ((pageWrapper?.clientHeight ?? 0) * 0.7).toString() ?? notice.height;

		if (parent) {
			if (parent.children.length > 1) {
				parent.insertBefore(notice, parent.children[1]);
			} 
			else {
				parent.appendChild(notice);
			}
			if (pageContent) {
				pageContent.remove();
			}
		}
	} else {
		//moodle 3.9
		notice.style.marginTop = "70px";
		const page = document.getElementById("page");
		page?.parentNode?.insertBefore(notice, page);
		page?.remove();
	}
}

function isQuizPreviewPageMoodle(): boolean {
	return window.location.pathname.endsWith("/quiz/view.php");
}

function hideQuizPreviewPageMoodle(): void {
	const quizWrapperDiv = document.getElementById(QUIZ_MOODLE.ID.WRAPPER_DIV);
	if (quizWrapperDiv) {
		quizWrapperDiv.style.display = "none";
	}
}

function hidePasswordText(): void {
	const passwordText = document.querySelector("div.box.py-3.quizinfo > p:nth-child(1)");
	if (
		passwordText instanceof HTMLElement &&
		passwordText.textContent?.includes("To attempt this quiz you need to know the quiz password")
	) {
		passwordText.style.display = "none";
	}
}

function showQuizPreviewPageMoodle(isProctored?: boolean): void {
	const quizWrapperDiv = document.getElementById(QUIZ_MOODLE.ID.WRAPPER_DIV);
	if (quizWrapperDiv) {
		quizWrapperDiv.style.removeProperty("display");
	}
	if (isProctored) hidePasswordText();
}

const isMoodleQuizAttemptPage = () => {
	const regex = /^https:\/\/[^\/]+\/mod\/quiz\/attempt.php.*/;
	return regex.test(window.location.href);
};

/** Check if the current user is an instructor on the quiz preview page */
const checkIsInstructorPreviewPage = () => {
	return (
		!!document.querySelector("#action-menu-toggle-3") ||
		document.querySelector("#usernavigation > form > div > label")?.textContent?.trim() === "Edit mode"
	);
};

/** Check if the current user is an instructor during quiz attempt */
const checkIsInstructorAttemptPage = () => {
	const ol = document.querySelector("#page-navbar > nav > ol");
	const containsPreviewLink = ol
		? Array.from(ol.querySelectorAll("a")).some((a) => a?.textContent?.trim() === "Preview")
		: false;

	const form = Array.from(document.querySelectorAll("form")).find(
		(f) => f instanceof HTMLFormElement && f.action.endsWith("startattempt.php"),
	);
	const containsStartPreviewButton = form?.querySelector("button")?.textContent?.trim() === "Start a new preview";

	return containsPreviewLink || containsStartPreviewButton;
};

async function startMoodlePlugin(): Promise<void> {
	try {
		const iframeHelper = await createThemeIframeHelper();
		listenForLTILoad("moodle");
		void sendInstituteId(iframeHelper, Number(window.instituteId));
		const isMoodleQuizPreviewPage = isQuizPreviewPageMoodle();

		const { hostname, courseId, quizId } = getMoodleQuizInfo() ?? {};
		const assessmentInfo: AssessmentQueryInfo = {
			instituteId: Number(window.instituteId),
			lmsProvidedCourseId: courseId,
			lmsProvidedAssessmentId: quizId,
		};
		const reformattedAssessmentInfo = {
			lms: "moodle" as Lms,
			hostname: hostname || "",
			courseId: courseId || undefined,
			quizId: quizId || "",
		};
		if (isMoodleQuizAttemptPage() && !checkIsInstructorAttemptPage()) {
			if (
				await checkReturnToAssessmentStart({
					assessmentInfo,
					extensionConnectIframe: iframeHelper,
				})
			) {
				const attemptUrl = new URL(window.location.href);
				const host = attemptUrl.host;
				const cmid = attemptUrl.searchParams.get("cmid");

				if (cmid) {
					// redirect to start (preview) page if the student doesn't have the extension
					window.location.href = `https://${host}/mod/quiz/view.php?id=${cmid}`;
				}
			}
		} else if (isMoodleQuizPreviewPage) {
			const isInstructor = checkIsInstructorPreviewPage();
			const saveUrl = () => {
				if (hostname && courseId && quizId) {
					void saveAssessmentUrl(iframeHelper, window.location.href, {
						lms: "moodle",
						quizId,
						courseId,
						hostname,
					});
				}
			};
			checkStartAssessment({
				getAssessmentInfo: () => assessmentInfo,
				extensionConnectIframe: iframeHelper,
				isInstructor,
				injectLoading: hideQuizPreviewPageMoodle,
				removeLoading: showQuizPreviewPageMoodle,
				injectUnsupportedBrowser: () => showBlockPageMoodle("browser"),
				injectInstallExtension: () => {
					observeAndRemoveExtensionIframes();
					showBlockPageMoodle("download");
					saveUrl();
				},
				injectUpdateExtension: () => {
					observeAndRemoveExtensionIframes();
					showBlockPageMoodle("update");
					saveUrl();
				},
				injectLaunchLockdown: (proctoringOptions) => {
					if (proctoringOptions) {
						modifyMoodleQuizEntryLockdown(reformattedAssessmentInfo, proctoringOptions, window.location.href);
					}
				},
				injectLaunchMobile: () => showBlockPageMoodle("mobile"),
				injectLockdownOrMobile: hidePasswordText,
			});
		}
	} catch (err) {
		console.error("YuJa Verity encountered an error: ", err);
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", function () {
		const environment = window.environment as string;
		verityHostname = environment === "staging" ? "staging.verity.yuja.com" : "zone1.verity.yuja.com";
		courseId = window.courseId as string;
		startMoodlePlugin().catch(console.error);
	});
} else {
	const environment = window.environment as string;
	verityHostname = environment === "staging" ? "staging.verity.yuja.com" : "zone1.verity.yuja.com";
	courseId = window.courseId as string;
	startMoodlePlugin().catch(console.error);
}
